import {
  Box,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { CommunicationModalHeader } from "@src/components/widgets/Modals/ModalCommunication/CommunicationModalHeader";
import { CommunicationTabParent } from "@src/components/widgets/Modals/ModalCommunication/components/tabs/communication-tab-parent";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { type PropsWithChildren, useRef } from "react";
import { CommentInput } from ".";

interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ModalWrapper = observer(function ModalWrapper({
  children,
  isOpen,
  onClose,
}: PropsWithChildren<ModalWrapperProps>) {
  const { taskDetailModalStore: store } = useStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const dividerColor = useColorModeValue("gray.300", "gray.600");
  const hoverColor = useColorModeValue("purple.700", "violet.300");

  return (
    <Modal
      blockScrollOnMount={false}
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent
        w="95vw"
        minW="95vw"
        maxW="95vw"
        h="95dvh"
        minH="95dvh"
        maxH="95dvh"
      >
        <ModalBody flexDir="column" flex="1" display="flex" p="1">
          <CommunicationModalHeader
            onClose={onClose}
            onTimeTrackingItemCreated={(trackedTime) => {
              store.disclosureState.additionalData?.onTimeTrackingItemCreated?.(
                trackedTime,
              );
            }}
          />
          <HStack
            ref={containerRef}
            pos="relative"
            flex="1"
            overflow="hidden"
            w="full"
            h="full"
            onMouseLeave={store.handleContainerMouseLeave}
            onMouseMove={(e) => store.handleContainerMouseMove(e, containerRef)}
            onMouseUp={store.handleContainerMouseUp}
            spacing="0"
          >
            <Flex
              ref={store.leftModalPanelRef}
              overflowY="auto"
              w={store.leftPanelWidthPercentage}
              h="full"
              maxH="full"
            >
              <Flex direction="column" flex="1">
                {children}
              </Flex>
            </Flex>

            {/* Draggable divider */}
            <Flex
              pos="relative"
              align="center"
              justify="center"
              w="6px"
              h="full"
              _hover={{
                "& > hr": {
                  bg: hoverColor,
                },
              }}
              userSelect="none"
              cursor="col-resize"
              onMouseDown={store.handleDividerMouseDown}
            >
              <Divider
                w="1px"
                h="full"
                bg={store.isDragging.value ? hoverColor : dividerColor}
                orientation="vertical"
              />
            </Flex>

            <Flex w={store.rightPanelWidthPercentage} h="full">
              <VStack align="start" justify="space-between" flex="1">
                <Box overflowY="auto" w="full" maxH="full">
                  <CommunicationTabParent />
                </Box>
                {store.task.value?.id && (
                  <Box w="full" p="2">
                    <CommentInput taskId={store.task.value.id} />
                  </Box>
                )}
              </VStack>
            </Flex>
          </HStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});
